// A merchant reply or a RecoveryAI email-channel reply, rendered as an
// email card regardless of author (docs/06-ux-rules.md "Thread": "channel
// decides the style, not the author"; P5 D2/D9). Avatar rule (FR-62 /
// docs/06-ux-rules.md "Avatar rule", v68): RecoveryAI/automated → Thanx
// mark, never initials; merchant/staff → the merchant's own SQUARE app
// icon, never initials (v68 changelog: "Merchant/staff email replies now
// show the restaurant's own logo as the avatar ... instead of text
// initials" — a staffer sends under the MERCHANT's brand, not their own).
//
// The icon comes from `useCurrentMerchant()`, not a per-item field —
// `buildThreadItems` only carries the replying staffer's display name
// (`authorName`); the brand icon is a single-tenant-dashboard concept, the
// same one every `merchant_email` item shares. Mirrors the
// `hasMerchantLogo`/`missing.png` masking and the real-logo-vs-placeholder-
// square treatment already established for exactly this concept in
// `scenes/LiveUpdates/components/preview/AppIcon.tsx` (not imported
// directly — scene boundary — but the same pattern, reused here rather
// than the shared `Avatar` component's circular silhouette/initials
// fallbacks, which are the wrong shape/semantics for a brand icon).
//
// RecoveryAI's name also renders as the brand wordmark image (v69
// changelog: "RecoveryAI email-card header now renders the branded
// wordmark lockup ... instead of hand-typed uppercase text"), reusing the
// existing `assets/images/recovery-ai/img_wordmark.svg` asset (already
// used by `scenes/OrderFeedback/settings/CurrentSetupCard.tsx` and
// `scenes/OrderFeedback/overview/PromoBlock.tsx`) rather than plain text.
//
// Layout follows Sketch v83's `.cv-email`: the avatar sits outside the
// card; the card has a solid header bar (mail icon · sender · time), then
// "to <guest> · subject", the body, and a "Sent by email" footer. Where a
// v83 colour fails WCAG AA, the nearest passing token stands in (noted
// inline).
import thanxMark from 'assets/images/order-feedback/thanx-setup-mark.png'
import imgWordmark from 'assets/images/recovery-ai/img_wordmark.svg'
import Envelope from 'components/Icons/Envelope'
import PaperPlane from 'components/Icons/PaperPlane'
import palette from 'constants/palette'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { formatShortDateTime } from 'utilities/dateFormatters'
import {
  EMAIL_CARD_WORDMARK_HEIGHT_PX,
  THREAD_ITEM_MAX_WIDTH_PX,
} from '../constants'

const t = buildTranslate('feedback_inbox.thread')

// v83 `.cv-email-av`: a 34px rounded square with a soft shadow.
const AVATAR_SIZE_PX = 34
const AVATAR_RADIUS_PX = 9
const AVATAR_SHADOW = '0 1px 2px rgba(20, 35, 46, 0.12)'
// v83 `.cv-email-card`.
const CARD_SHADOW = '0 1px 2px rgba(20, 35, 46, 0.04)'
const MAIL_ICON_WIDTH_PX = 15
const SENT_ICON_SIZE_PX = 13

// Header bar per author (v83 `.cv-email-head` / `.cv-email-card.rai`).
// Merchant: v83's #7c8794 is 3.65:1 behind white text, so grey70 (4.75:1)
// stands in. RecoveryAI: v83's #9aa4ae time is 2.33:1 on spearmint10, so
// grey90 stands in; the mail icon is v83's teal (spearmint70, 3.9:1 — icon
// contrast).
const HEAD_STYLE = {
  merchant: {
    background: palette.grey70,
    text: palette.white,
    time: palette.white,
    icon: palette.white,
  },
  ai: {
    background: palette.spearmint10,
    text: palette.grey90,
    time: palette.grey90,
    icon: palette.spearmint70,
  },
}

export interface EmailCardAuthor {
  kind: 'merchant' | 'ai'
  name: string
}

export interface EmailCardProps {
  author: EmailCardAuthor
  to: string
  subject: string
  body: string
  at: string
}

const EmailCard = ({ author, to, subject, body, at }: EmailCardProps) => {
  const [css] = useStyletron()
  const merchant = useCurrentMerchant()
  // A blank-reply subject serializes as "" (AI-1415) — drop the ` · ` suffix.
  const hasSubject = subject.trim() !== ''
  const merchantLogoUrl = merchant?.logo_image_urls?.default
  // Rails' Paperclip-style default asset for an unset attachment, not a
  // real uploaded logo (same check as LiveUpdates/index.tsx's own
  // `hasMerchantLogo`).
  const hasMerchantLogo =
    !!merchantLogoUrl && !/\/missing\.\w+(\?|$)/.test(merchantLogoUrl)
  const head = HEAD_STYLE[author.kind]

  const avatarImageClass = css({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  })

  return (
    <div
      className={css({
        display: 'flex',
        alignItems: 'flex-start',
        gap: '11px',
      })}
    >
      <div
        aria-hidden="true"
        className={css({
          flexShrink: 0,
          width: `${AVATAR_SIZE_PX}px`,
          height: `${AVATAR_SIZE_PX}px`,
          borderRadius: `${AVATAR_RADIUS_PX}px`,
          overflow: 'hidden',
          boxShadow: AVATAR_SHADOW,
          // Merchant/staff: a plain neutral square when no logo has been
          // uploaded — never initials (FR-62).
          backgroundColor: palette.grey20,
        })}
      >
        {author.kind === 'ai' ? (
          <img src={thanxMark} alt="" className={avatarImageClass} />
        ) : (
          hasMerchantLogo && (
            <img src={merchantLogoUrl} alt="" className={avatarImageClass} />
          )
        )}
      </div>
      <div
        className={css({
          flex: 1,
          minWidth: 0,
          maxWidth: `${THREAD_ITEM_MAX_WIDTH_PX}px`,
          border: `1px solid ${palette.grey20}`,
          borderRadius: '10px',
          overflow: 'hidden',
          backgroundColor: palette.white,
          boxShadow: CARD_SHADOW,
        })}
      >
        <div
          data-testid="email-card-head"
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: '9px',
            padding: '9px 15px',
            backgroundColor: head.background,
            color: head.text,
            fontSize: '13.5px',
            fontWeight: 800,
          })}
        >
          <span
            aria-hidden="true"
            className={css({ display: 'flex', flexShrink: 0 })}
          >
            <Envelope
              fillColor={head.icon}
              className={css({
                width: `${MAIL_ICON_WIDTH_PX}px`,
                height: 'auto',
              })}
            />
          </span>
          {author.kind === 'ai' ? (
            <img
              src={imgWordmark}
              alt={author.name}
              className={css({
                height: `${EMAIL_CARD_WORDMARK_HEIGHT_PX}px`,
                width: 'auto',
                display: 'block',
              })}
            />
          ) : (
            <span
              className={css({
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              })}
            >
              {author.name}
            </span>
          )}
          <span
            className={css({
              marginLeft: 'auto',
              fontSize: '11px',
              fontWeight: 600,
              color: head.time,
              whiteSpace: 'nowrap',
            })}
          >
            {formatShortDateTime(at)}
          </span>
        </div>
        <div className={css({ padding: '14px 16px' })}>
          {/* v83 uses grey60 (3.85:1 on white); grey70 passes AA. */}
          <div
            data-testid="email-card-to"
            className={css({
              fontSize: '12px',
              color: palette.grey70,
              marginBottom: '9px',
            })}
          >
            {t('email_to')} <b className={css({ fontWeight: 700 })}>{to}</b>
            {hasSubject && <> {t('email_subject', { subject })}</>}
          </div>
          <div
            className={css({
              fontSize: '14.5px',
              lineHeight: 1.55,
              color: palette.grey90,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            })}
          >
            {body}
          </div>
          {/* v83 uses grey50 (2.27:1 on white); grey70 passes AA. */}
          <div
            className={css({
              display: 'inline-flex',
              alignItems: 'center',
              gap: '7px',
              marginTop: '12px',
              fontSize: '12px',
              fontWeight: 600,
              color: palette.grey70,
            })}
          >
            <PaperPlane
              fillColor={palette.grey70}
              className={css({
                width: `${SENT_ICON_SIZE_PX}px`,
                height: `${SENT_ICON_SIZE_PX}px`,
              })}
            />
            <span>{t('sent_by_email')}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmailCard
